// packages/web/src/components/AdminLayout.tsx
import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { AdminSidebar, ADMIN_TABS, tabFromPath, AdminMobileMenuButton } from './AdminSidebar'
import { useAuth } from '../context/AuthContext'
import { LogOut, ExternalLink, ShieldCheck, User as UserIcon, Sun, Moon } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'
import { AUTH_TOKEN_KEY, AUTH_USER_KEY } from '../lib/authStorage'

export const AdminLayout: React.FC = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { pathname, searchStr } = useLocation()
  const navigate = useNavigate()
  const activeTab = tabFromPath(pathname)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const isAuthed = (isAuthenticated && isAdmin) || (() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    const uStr = localStorage.getItem(AUTH_USER_KEY)
    if (!token || !uStr) return false
    try {
      const u = JSON.parse(uStr)
      return u?.role === 'admin' || u?.role === 'superadmin'
    } catch {
      return false
    }
  })()

  useEffect(() => {
    if (!isAuthed) {
      navigate({
        to: '/login',
        search: { redirect: pathname + searchStr },
        replace: true,
      })
    }
  }, [isAuthed, pathname, searchStr, navigate])

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileNavOpen(false)
  }, [pathname])

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileNavOpen])

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex flex-col items-center justify-center space-y-4 px-6">
        <div className="w-10 h-10 border-2 border-[var(--gold-main)] border-t-transparent rounded-full animate-spin" />
        <p className="text-xs text-[var(--text-muted)] text-center">
          Verifying administrative access privileges...
        </p>
      </div>
    )
  }

  const handleLogout = () => {
    logout()
    navigate({ to: '/login', replace: true })
  }

  const activeLabel = ADMIN_TABS.find((t) => t.key === activeTab)?.label ?? 'Dashboard'

  return (
    <div className="min-h-screen bg-[var(--bg-main)] font-sans text-[var(--text-primary)]">
      <AdminSidebar
        mobileOpen={mobileNavOpen}
        onMobileOpen={() => setMobileNavOpen(true)}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      <div className="md:ml-[240px] admin-main-shell flex flex-col min-h-screen min-w-0">
        <header className="admin-print-hidden sticky top-0 z-30 bg-[var(--bg-main)]/95 backdrop-blur-xl border-b border-[var(--border)] px-3 sm:px-8 py-2.5 sm:py-3.5 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0 flex-1">
            <AdminMobileMenuButton onClick={() => setMobileNavOpen(true)} />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold font-title text-[var(--text-primary)] tracking-tight truncate">
                {activeLabel}
              </h1>
              <p className="hidden sm:block text-[11px] text-[var(--text-faint)] truncate">
                Buddy&apos;s Choice — Luxury Jewellery Admin Portal
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              className="p-2 text-[var(--text-muted)] hover:text-[var(--gold-main)] transition rounded-lg hover:bg-[var(--bg-surface-2)] min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>

            <div className="hidden lg:flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] text-xs max-w-[200px]">
              <div className="w-7 h-7 rounded-full bg-[var(--gold-main)]/20 border border-[var(--gold-main)]/40 flex items-center justify-center text-[var(--gold-main)] font-bold shrink-0">
                <UserIcon className="w-3.5 h-3.5" />
              </div>
              <div className="text-left min-w-0">
                <div className="font-semibold text-[var(--text-primary)] truncate">
                  {user?.name || user?.email || 'Administrator'}
                </div>
                <div className="text-[9px] text-[var(--gold-main)] font-mono uppercase tracking-wider flex items-center gap-1">
                  <ShieldCheck className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate">{user?.role || 'ADMIN'}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate({ to: '/' })}
              className="btn-gold-outline text-xs py-1.5 px-2 sm:px-3 flex items-center gap-1.5 min-h-[40px]"
              title="Open Customer Storefront"
            >
              <ExternalLink className="w-3.5 h-3.5 text-[var(--gold-main)] shrink-0" />
              <span className="hidden sm:inline">Storefront</span>
            </button>

            <button
              onClick={handleLogout}
              className="p-2 text-[var(--text-faint)] hover:text-red-400 hover:bg-[var(--bg-surface-2)] rounded-xl transition min-w-[40px] min-h-[40px] flex items-center justify-center"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <main className="admin-main-content flex-1 px-3 sm:px-8 lg:px-10 py-4 sm:py-6 pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-10 max-w-[1600px] w-full mx-auto min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
